"use client"

import { useEffect, useMemo, useState } from "react"
import services from "../../../API"
import { Check, ChevronDown, CreditCard, Info, Plus, RefreshCcw, Star, X } from "lucide-react";

const creditPacks = [
    { credits: 1000, price: 20, savingsPercent: 10 },
    { credits: 2000, price: 40, savingsPercent: 20 },
    { credits: 5000, price: 100, savingsPercent: 30 },
]

export function ExtraCreditsPopup({
    brandName = "Zimzee",
    darkMode = false,
    onToggleTheme,
    // External control props (optional)
    isOpen: isOpenProp,
    onClose: onCloseProp,
}) {
    const [isOpenInternal, setIsOpenInternal] = useState(true)
    // If isOpenProp is provided, use it; otherwise use internal state
    const isOpen = isOpenProp !== undefined ? isOpenProp : isOpenInternal

    const [state, setState] = useState("select")
    const [selectedPack, setSelectedPack] = useState(creditPacks[1])
    const [threshold, setThreshold] = useState("5")
    const [topUpAmount, setTopUpAmount] = useState("15")
    const [showPaymentModal, setShowPaymentModal] = useState(false)
    const [fullName, setFullName] = useState("Nicky Kalliongis")
    const [country, setCountry] = useState("United States")
    const [address, setAddress] = useState("")
    const [cardNumber, setCardNumber] = useState("4242 4242 4242 4242")
    const [expiry, setExpiry] = useState("")
    const [cvc, setCvc] = useState("")
    const [differentName, setDifferentName] = useState(false)
    const [isCheckingOut, setIsCheckingOut] = useState(false)

    // Reset modal state whenever it is re-opened
    useEffect(() => {
        if (isOpen) {
            setState("select")
            setShowPaymentModal(false)
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen || state === "select" || state === "auto-reload") {
            return
        }

        if (state === "confirming") {
            const timer = window.setTimeout(() => setState("processing"), 2000)
            return () => window.clearTimeout(timer)
        }

        if (state === "processing") {
            const timer = window.setTimeout(() => setState("adding"), 2000)
            return () => window.clearTimeout(timer)
        }

        if (state === "adding") {
            const timer = window.setTimeout(() => setState("almost-done"), 1500)
            return () => window.clearTimeout(timer)
        }

        if (state === "almost-done") {
            const timer = window.setTimeout(() => setState("success"), 1500)
            return () => window.clearTimeout(timer)
        }

        if (state === "success") {
            const timer = window.setTimeout(() => setState("auto-reload"), 2000)
            return () => window.clearTimeout(timer)
        }
    }, [isOpen, state])

    const pricing = useMemo(() => {
        const discount = (selectedPack.price * selectedPack.savingsPercent) / 100
        const estimatedTax = (selectedPack.price - discount) * 0.08
        const totalDue = selectedPack.price - discount + estimatedTax

        return { discount, estimatedTax, totalDue }
    }, [selectedPack])

    const closePopup = () => {
        if (onCloseProp) {
            onCloseProp()
        } else {
            setIsOpenInternal(false)
        }
        setState("select")
        setShowPaymentModal(false)
    }

    const reopenPopup = () => {
        setIsOpenInternal(true)
        setState("select")
    }

    const handlePayNow = async () => {
        setIsCheckingOut(true)
        try {
            const response = await services.createTopupCheckout(selectedPack.credits)
            // The API returns a checkout/redirect URL — open it
            const checkoutUrl = response?.data?.url || response?.url
            if (checkoutUrl) {
                window.location.href = checkoutUrl
            }
        } catch {
            // Error toast is already shown by callApi
        } finally {
            setIsCheckingOut(false)
        }
    }

    return (
        <section
            className={[
                "min-h-screen bg-neutral-100 text-neutral-900 dark:bg-neutral-950 dark:text-white",
                darkMode ? "dark" : "",
            ].join(" ")}
        >
            <div className="relative min-h-screen">
                <div className="fixed right-4 top-4 z-[80]">
                    <button
                        className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 dark:bg-white dark:text-neutral-900"
                        onClick={onToggleTheme}
                        type="button"
                    >
                        {darkMode ? "Light Mode" : "Dark Mode"}
                    </button>
                </div>

                <div className="flex min-h-screen items-start justify-center px-6 pb-6 pt-16">
                    <div className="w-full max-w-md">
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">Billing</p>
                        <h1 className="mt-3 text-3xl font-semibold tracking-[-0.03em]">
                            Usage settings
                        </h1>
                        <p className="mt-2 text-sm leading-6 text-neutral-500 dark:text-neutral-400">
                            Standalone recreation of the extra credits popup, ready to move into your React
                            app with Tailwind classes only.
                        </p>
                        {!isOpen ? (
                            <button
                                className="mt-6 rounded-lg bg-neutral-900 px-4 py-3 text-sm font-medium text-white transition hover:opacity-90 dark:bg-white dark:text-neutral-900"
                                onClick={reopenPopup}
                                type="button"
                            >
                                Open Credit Pack Modal
                            </button>
                        ) : null}
                    </div>
                </div>

                {isOpen ? (
                    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
                        <button
                            aria-label="Close popup"
                            className="absolute inset-0 bg-black/20 backdrop-blur-sm dark:bg-black/60"
                            onClick={closePopup}
                            type="button"
                        />

                        <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-2xl dark:border-neutral-700 dark:bg-neutral-900">
                            {state === "select" ? (
                                <div className="p-6">
                                    <div className="mb-4 flex items-start justify-between">
                                        <BrandLogo brandName={brandName} darkMode={darkMode} />
                                        <button
                                            className="text-neutral-500 transition hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                                            onClick={closePopup}
                                            type="button"
                                        >
                                            <X className="h-5 w-5" />
                                        </button>
                                    </div>

                                    <h2 className="mb-1 text-xl font-semibold">Buy extra credits</h2>
                                    <p className="mb-6 text-sm text-neutral-500 dark:text-neutral-400">
                                        Choose an amount to start. You can always buy more later.
                                    </p>

                                    <div className="mb-6 flex gap-3 max-sm:flex-col">
                                        {creditPacks.map((pack) => {
                                            const isActive = selectedPack.credits === pack.credits

                                            return (
                                                <button
                                                    key={pack.credits}
                                                    className={[
                                                        "flex-1 overflow-hidden rounded-xl border-2 text-left transition-all",
                                                        isActive
                                                            ? "border-neutral-900 dark:border-white"
                                                            : "border-neutral-200 hover:border-neutral-400 dark:border-neutral-700 dark:hover:border-neutral-500",
                                                    ].join(" ")}
                                                    onClick={() => setSelectedPack(pack)}
                                                    type="button"
                                                >
                                                    <div className="p-3">
                                                        <div className="text-lg font-semibold">${pack.price}</div>
                                                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                                            {pack.credits.toLocaleString()} credits
                                                        </div>
                                                    </div>
                                                    <div
                                                        className={[
                                                            "px-3 py-1.5 text-xs font-medium",
                                                            isActive
                                                                ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900"
                                                                : "bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400",
                                                        ].join(" ")}
                                                    >
                                                        Save {pack.savingsPercent}%
                                                    </div>
                                                </button>
                                            )
                                        })}
                                    </div>

                                    <div className="mb-6 space-y-3 rounded-xl border border-neutral-200 p-4 dark:border-neutral-700">
                                        <div className="flex justify-between text-sm">
                                            <span>Extra credits</span>
                                            <span>${selectedPack.price.toFixed(2)}</span>
                                        </div>

                                        <div className="flex justify-between text-sm">
                                            <span className="flex items-center gap-1">
                                                Discount
                                                <span className="group relative inline-flex">
                                                    <button
                                                        className="text-neutral-500 dark:text-neutral-500"
                                                        type="button"
                                                    >
                                                        <Info className="h-3.5 w-3.5" />
                                                    </button>
                                                    <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-52 -translate-x-1/2 rounded-md bg-neutral-900 px-3 py-2 text-xs text-white opacity-0 transition group-hover:opacity-100 dark:bg-white dark:text-neutral-900">
                                                        You can save up to $2,000 every billing cycle
                                                    </span>
                                                </span>
                                            </span>
                                            <span>
                                                Save {selectedPack.savingsPercent}% (${pricing.discount.toFixed(2)})
                                            </span>
                                        </div>

                                        <div className="flex justify-between text-sm">
                                            <span>Estimated tax</span>
                                            <span>${pricing.estimatedTax.toFixed(2)}</span>
                                        </div>

                                        <div className="border-t border-neutral-200 pt-2 dark:border-neutral-700">
                                            <div className="flex justify-between text-sm font-semibold">
                                                <span>Total due</span>
                                                <span className="text-lg">${pricing.totalDue.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* PAYMENT METHOD — temporarily hidden
                                    <div className="mb-6 rounded-xl border border-neutral-200 p-4 dark:border-neutral-700">
                                        <div className="flex items-center justify-between gap-3 max-sm:flex-col max-sm:items-start">
                                            <span className="text-sm font-medium">Payment method</span>
                                            <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
                                                <CreditCard className="h-5 w-5" />
                                                <span>Visa .... {lastFourDigits(cardNumber)}</span>
                                                <button
                                                    className="transition hover:text-neutral-900 dark:hover:text-white"
                                                    onClick={() => setShowPaymentModal(true)}
                                                    type="button"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    */}

                                    <button
                                        className="h-12 w-full rounded-xl bg-neutral-900 text-base font-medium text-white transition hover:opacity-90 dark:bg-white dark:text-neutral-900 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        onClick={handlePayNow}
                                        disabled={isCheckingOut}
                                        type="button"
                                    >
                                        {isCheckingOut ? (
                                            <>
                                                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                                </svg>
                                                Processing...
                                            </>
                                        ) : (
                                            <>Pay ${pricing.totalDue.toFixed(2)} now</>
                                        )}
                                    </button>

                                    <p className="mt-4 text-center text-xs text-neutral-500 dark:text-neutral-500">
                                        By clicking Pay now, you allow {brandName} to charge your card in the amount
                                        above.
                                    </p>
                                </div>
                            ) : null}

                            {state === "confirming" ? (
                                <ProgressScreen
                                    icon={<StatusCardBadge icon={<Check className="h-6 w-6" />} />}
                                    title="Confirming your purchase..."
                                />
                            ) : null}

                            {state === "processing" ? (
                                <ProgressScreen
                                    icon={<StatusCardBadge icon={<RefreshCcw className="h-6 w-6" />} />}
                                    title="Processing payment..."
                                />
                            ) : null}

                            {state === "adding" ? (
                                <ProgressScreen
                                    icon={<StatusCardBadge icon={<Plus className="h-6 w-6" />} />}
                                    title="Adding credit to your account..."
                                />
                            ) : null}

                            {state === "almost-done" ? (
                                <ProgressScreen
                                    icon={<StatusCardBadge icon={<ClockBadge />} />}
                                    title="Almost done..."
                                />
                            ) : null}

                            {state === "success" ? (
                                <ProgressScreen
                                    icon={<StatusCardBadge icon={<Star className="h-6 w-6" />} />}
                                    title="Purchase successful"
                                />
                            ) : null}

                            {state === "auto-reload" ? (
                                <div className="p-6">
                                    <div className="mb-6 flex items-center justify-between gap-4">
                                        <h2 className="text-xl font-semibold">Turn on auto-reload?</h2>
                                        <button
                                            className="text-neutral-500 transition hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                                            onClick={closePopup}
                                            type="button"
                                        >
                                            <X className="h-5 w-5" />
                                        </button>
                                    </div>

                                    <p className="mb-6 text-sm text-neutral-500 dark:text-neutral-400">
                                        You can automatically buy more extra usage when you run low.
                                    </p>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="mb-2 block text-sm font-medium" htmlFor="threshold">
                                                When extra usage is:
                                            </label>
                                            <input
                                                id="threshold"
                                                className="h-10 w-full rounded-md border border-neutral-200 bg-neutral-100 px-3 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800"
                                                onChange={(event) =>
                                                    setThreshold(event.target.value.replace(/[^0-9]/g, ""))
                                                }
                                                value={`$${threshold}`}
                                            />
                                        </div>

                                        <div>
                                            <label className="mb-2 block text-sm font-medium" htmlFor="top-up-amount">
                                                Top up to:
                                            </label>
                                            <input
                                                id="top-up-amount"
                                                className="h-10 w-full rounded-md border border-neutral-200 bg-neutral-100 px-3 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800"
                                                onChange={(event) =>
                                                    setTopUpAmount(event.target.value.replace(/[^0-9]/g, ""))
                                                }
                                                value={`$${topUpAmount}`}
                                            />
                                        </div>
                                    </div>

                                    <p className="mb-6 mt-6 text-sm leading-6 text-neutral-500 dark:text-neutral-500">
                                        You agree that {brandName} will charge the card you have on file in the amount
                                        above on a recurring basis whenever your balance reaches the amount indicated.
                                        To cancel, turn off auto-reload.
                                    </p>

                                    <div className="flex justify-end gap-3 max-sm:flex-col">
                                        <button
                                            className="rounded-lg border border-neutral-300 px-6 py-2.5 text-sm font-medium dark:border-neutral-600"
                                            onClick={closePopup}
                                            type="button"
                                        >
                                            Later
                                        </button>
                                        <button
                                            className="rounded-lg bg-neutral-900 px-6 py-2.5 text-sm font-medium text-white dark:bg-white dark:text-neutral-900"
                                            onClick={closePopup}
                                            type="button"
                                        >
                                            Turn on
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        {showPaymentModal ? (
                            <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
                                <button
                                    aria-label="Close payment modal"
                                    className="absolute inset-0 bg-black/50"
                                    onClick={() => setShowPaymentModal(false)}
                                    type="button"
                                />

                                <div className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white shadow-2xl dark:bg-neutral-900">
                                    <div className="p-6">
                                        <h3 className="mb-6 text-lg font-semibold">Payment method</h3>

                                        <div className="mb-4">
                                            <label className="mb-1.5 block text-sm font-medium" htmlFor="full-name">
                                                Full name
                                            </label>
                                            <input
                                                id="full-name"
                                                className="h-10 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800"
                                                onChange={(event) => setFullName(event.target.value)}
                                                value={fullName}
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label className="mb-1.5 block text-sm font-medium" htmlFor="country">
                                                Country or region
                                            </label>
                                            <div className="relative">
                                                <select
                                                    id="country"
                                                    className="h-10 w-full appearance-none rounded-md border border-neutral-200 bg-white px-3 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800"
                                                    onChange={(event) => setCountry(event.target.value)}
                                                    value={country}
                                                >
                                                    <option>United States</option>
                                                    <option>Canada</option>
                                                    <option>United Kingdom</option>
                                                    <option>Australia</option>
                                                    <option>Germany</option>
                                                    <option>France</option>
                                                </select>
                                                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
                                            </div>
                                        </div>

                                        <div className="mb-6">
                                            <label className="mb-1.5 block text-sm font-medium" htmlFor="address">
                                                Address
                                            </label>
                                            <input
                                                id="address"
                                                className="h-10 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800"
                                                onChange={(event) => setAddress(event.target.value)}
                                                placeholder="Street address"
                                                value={address}
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label className="mb-1.5 block text-sm font-medium" htmlFor="card-number">
                                                Card number
                                            </label>
                                            <div className="relative">
                                                <input
                                                    id="card-number"
                                                    className="h-10 w-full rounded-md border border-neutral-200 bg-white px-3 pr-28 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800"
                                                    onChange={(event) => setCardNumber(event.target.value)}
                                                    placeholder="1234 1234 1234 1234"
                                                    value={cardNumber}
                                                />
                                                <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
                                                    <span className="flex h-5 w-8 items-center justify-center rounded bg-[#1A1F71] text-[8px] font-bold italic text-white">
                                                        VISA
                                                    </span>
                                                    <span className="flex h-5 w-8 items-center justify-center rounded bg-neutral-800">
                                                        <span className="flex -space-x-1">
                                                            <span className="h-3 w-3 rounded-full bg-red-500" />
                                                            <span className="h-3 w-3 rounded-full bg-yellow-500" />
                                                        </span>
                                                    </span>
                                                    <span className="flex h-5 w-8 items-center justify-center rounded bg-[#006FCF] text-[6px] font-bold text-white">
                                                        AMEX
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-6 flex gap-3 max-sm:flex-col">
                                            <div className="flex-1">
                                                <label className="mb-1.5 block text-sm font-medium" htmlFor="expiry">
                                                    Expiration date
                                                </label>
                                                <input
                                                    id="expiry"
                                                    className="h-10 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800"
                                                    onChange={(event) => setExpiry(event.target.value)}
                                                    placeholder="MM / YY"
                                                    value={expiry}
                                                />
                                            </div>

                                            <div className="flex-1">
                                                <label className="mb-1.5 block text-sm font-medium" htmlFor="cvc">
                                                    Security code
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        id="cvc"
                                                        className="h-10 w-full rounded-md border border-neutral-200 bg-white px-3 pr-10 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800"
                                                        onChange={(event) => setCvc(event.target.value)}
                                                        placeholder="CVC"
                                                        value={cvc}
                                                    />
                                                    <CreditCard className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
                                                </div>
                                            </div>
                                        </div>

                                        <label className="mb-6 flex cursor-pointer items-center gap-2 text-sm">
                                            <input
                                                checked={differentName}
                                                className="h-4 w-4"
                                                onChange={(event) => setDifferentName(event.target.checked)}
                                                type="checkbox"
                                            />
                                            <span>Use a different name on invoices</span>
                                        </label>

                                        <div className="flex justify-end gap-3 max-sm:flex-col">
                                            <button
                                                className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium dark:border-neutral-700"
                                                onClick={() => setShowPaymentModal(false)}
                                                type="button"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white dark:bg-white dark:text-neutral-900"
                                                onClick={() => setShowPaymentModal(false)}
                                                type="button"
                                            >
                                                Update
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </section>
    )
}

function ProgressScreen({ icon, title }) {
    return (
        <div className="flex min-h-[320px] flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mb-6 h-24 w-24 text-neutral-900/80 dark:text-white/80">{icon}</div>
            <h2 className="text-xl font-semibold">{title}</h2>
        </div>
    )
}

function BrandLogo({ brandName, darkMode }) {
    return (
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neutral-900 text-[22px] font-bold text-white dark:bg-white dark:text-neutral-900">
            {darkMode ? brandName.charAt(0).toUpperCase() : brandName.charAt(0).toUpperCase()}
        </div>
    )
}

function StatusCardBadge({ icon }) {
    return (
        <div className="relative h-full w-full">
            <svg viewBox="0 0 64 64" fill="none" className="h-full w-full">
                <rect
                    x="6"
                    y="16"
                    width="44"
                    height="32"
                    rx="4"
                    stroke="currentColor"
                    strokeWidth="2.5"
                />
                <path d="M6 26h44" stroke="currentColor" strokeWidth="2.5" />
                <path d="M12 36h16" stroke="currentColor" strokeLinecap="round" strokeWidth="2.5" />
                <path d="M12 42h8" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
                <circle cx="50" cy="42" r="10" stroke="currentColor" strokeWidth="2.5" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-end pr-[3px] pt-[18px]">
                <div className="flex h-5 w-5 items-center justify-center">{icon}</div>
            </div>
        </div>
    )
}

function ClockBadge() {
    return (
        <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
            <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
            <path d="M12 8v4l2.5 1.5" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
        </svg>
    )
}



/**
 * Lightweight wrapper — renders only the modal overlay (no surrounding page).
 * Props:
 *   isOpen  {boolean}  – whether to show the modal
 *   onClose {Function} – called when the user dismisses the modal
 */
export function ExtraCreditsModal({ isOpen, onClose }) {
    if (!isOpen) return null
    return (
        <ExtraCreditsPopup
            isOpen={isOpen}
            onClose={onClose}
        />
    )
}
